import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/core/avatar.tsx";
import { BadgeWrapper } from "@/components/core/badge.tsx";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table.tsx";
import Pagination from "@/components/core/pagination.tsx";
import { Searchbar } from "@/components/core/searchbar.tsx";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { usersQueryOptions } from "@/features/dashboard/api/queries/user-queries";
import type { User } from "@/features/dashboard/types/user";
import { formatDate } from "@/utils/format-date.ts";
import { IconChevronDown, IconPlus } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

export const Route = createFileRoute("/_dashboard/users")({
	validateSearch: z.object({
		status: z.string().optional(),
		search: z.string().optional(),
		per_page: z.number().optional(),
		page: z.number().nullable().optional(),
	}),
	loaderDeps: ({ search }) => ({
		page: search.page,
		per_page: search.per_page,
		status: search.status,
		search: search.search,
	}),
	loader: ({ context: { queryClient }, deps }) => {
		return queryClient.ensureQueryData(
			usersQueryOptions({
				...deps,
				include: "roles",
			}),
		);
	},
	component: Page,
});

const columns: ColumnDef<User>[] = [
	{
		id: "full_name",
		label: "Name",
		cell: ({ data }) => (
			<Link
				to="/users/$userId"
				params={{
					userId: data.id,
				}}
				className="font-semibold text-blue-600 hover:text-blue-500"
			>
				<div className="flex flex-row gap-2">
					<Avatar className="w-[32px] h-[32px] rounded-full bg-slate-300">
						<AvatarFallback className="text-sm">{data.initials}</AvatarFallback>
					</Avatar>
					<div className="flex flex-col justify-center">
						<div>{data.full_name}</div>
						<div className="text-gray-500 font-normal">{data.email}</div>
					</div>
				</div>
			</Link>
		),
	},
	{
		id: "roles",
		label: "Roles",
		cell: ({ data }) => data.roles.map((role) => role.name),
	},
	{
		id: "status",
		label: "Status",
		cell: ({ data }) => (
			<BadgeWrapper
				colorMapping={{
					Active: "green-light",
					Disabled: "red-light",
					Pending: "default",
				}}
				value={data.status}
			/>
		),
	},
	{
		id: "last_login",
		label: "Last Login",
		cellClassName: "text-right",
		headerClassName: "text-right",
		cell: ({ data }) => formatDate(data?.last_login_at) || "",
	},
];

function Page() {
	const search = Route.useSearch();
	const usersQuery = useSuspenseQuery(
		usersQueryOptions({
			...search,
			include: "roles",
		}),
	);
	const users = usersQuery.data.data;
	const navigate = useNavigate();
	const handleEdit = async (row: User) => {
		await navigate({
			to: "/users/$userId",
			params: { userId: row.id },
		});
	};

	return (
		<Content pageName="Users">
			{/*<Header title="Users" />*/}
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">Users</span>
						<span className="text-base text-foreground/70">Brief description for help here</span>
					</div>
					<DropdownMenu modal={false}>
						<DropdownMenuTrigger asChild>
							<Button>
								Actions
								<IconChevronDown size={16} stroke={2} />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent>
							<DropdownMenuGroup>
								<DropdownMenuItem
									onSelect={() => {
										navigate({
											to: "/user/create",
										});
									}}
								>
									<div className="flex flex-row items-center w-full py-1 px-2 gap-1">
										<IconPlus size={16} stroke={2} />
										<span className="inline-flex">New Record</span>
									</div>
								</DropdownMenuItem>
							</DropdownMenuGroup>
						</DropdownMenuContent>
					</DropdownMenu>
				</div>
				<div className="flex flex-col w-full gap-4">
					<Searchbar
						defaultValue={search.search}
						onChange={async (value) => {
							await navigate({
								to: "/users",
								search: (old) => ({
									...old,
									search: value,
									page: 1,
								}),
							});
						}}
					/>
					<DataTable showToolbar={false} data={users} columns={columns} showBorder={true} compact={true} onEdit={handleEdit} />
					<Pagination
						onClick={async (page) => {
							await navigate({
								to: "/users",
								search: (old) => ({
									...old,
									page,
								}),
							});
						}}
						initialPage={usersQuery.data.meta.current_page}
						pageCount={usersQuery.data.meta.last_page}
					/>
				</div>
			</div>
		</Content>
	);
}
